import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  isDevMode,
  output,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { EmployeeBenefitDocumentType } from '@flexyoo/api/models';
import { EmployeeCartDocumentsDataAccessService } from '@flexyoo/employee/data-access';
import {
  EmployeeCartBenefit,
  EmployeeCartDocument,
  EmployeeCartDocumentType,
  QuillSignStatus,
} from '@flexyoo/employee/models';
import { quillBenefitAddendumSignNotificationsActions } from '@flexyoo/employee/store';
import { hasDocument } from '@flexyoo/employee/util';
import { BenefitStatusEnum } from '@flexyoo/models';
import { FlexyooLetDirective } from '@flexyoo/shared/directives';
import { blobToText, openExternalUrl } from '@flexyoo/shared/functions';
import type { SignWithQuillResultDto } from '@flexyoo/api/models';
import { HttpErrorHandlerService, QuillDataAccessService } from '@flexyoo/shared/services';
import { LoaderComponent } from '@flexyoo/shared/ui';
import { Actions, ofType } from '@ngrx/effects';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  Observable,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { MbpEmployeeBasketBenefit } from '../../../mobility-budget/mbp-employee-benefits-overview/models';

@Component({
  selector: 'app-benefit-addendum-sign',
  templateUrl: './benefit-addendum-sign.component.html',
  styleUrl: './benefit-addendum-sign.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIcon,
    TranslateModule,
    AsyncPipe,
    LoaderComponent,
    FlexyooLetDirective,
  ],
})
export class BenefitAddendumSignComponent {
  readonly benefit = input<EmployeeCartBenefit | MbpEmployeeBasketBenefit | null>(null);
  readonly cartId = input<number | null>(null);
  readonly documents = input<readonly EmployeeCartDocument[]>([]);
  readonly quillDocumentId = input<number | null>(null);
  readonly documentType = input<'addendum' | 'declaration'>('addendum');

  readonly benefitAddendumSignStatusChanged = output<boolean>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly httpErrorHandlerService = inject(HttpErrorHandlerService);
  private readonly quillService = inject(QuillDataAccessService);
  private readonly cartDocumentsService = inject(EmployeeCartDocumentsDataAccessService);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly quillDocumentStatus$ = new BehaviorSubject<string>('');
  protected readonly quillDocumentId$ = new BehaviorSubject<number | null>(null);

  constructor() {
    inject(Actions)
      .pipe(
        ofType(quillBenefitAddendumSignNotificationsActions.received),
        withLatestFrom(this.quillDocumentId$),
        filter(([action, docId]) => action.notification?.quillDocumentId === docId),
        takeUntilDestroyed()
      )
      .subscribe(([action]) => this.processQuillStatus(action.notification.quillStatus));

    effect(() => {
      if (isDevMode() && this.benefit() && this.cartId() !== null) {
        console.warn(
          'BenefitAddendumSignComponent: both benefit and cartId provided; cartId takes precedence.'
        );
      }
    });

    effect(() => {
      const docId = this.quillDocumentId();
      this.quillDocumentId$.next(docId);

      // Once the addendum is stored there is nothing left to process on Quill's side
      // (e.g. reviewing a confirmed benefit), so only check the status during the sign flow.
      if (docId && untracked(() => this.showSignFlow())) {
        this.updateStatus(docId);
      }
    });
  }

  protected showConfirmButton(): boolean {
    return (
      !this.isCartMode() &&
      this.hasAddendumDocument() &&
      this.benefit()?.BenefitState === BenefitStatusEnum.Selected
    );
  }

  protected showSignFlow(): boolean {
    return !this.hasAddendumDocument();
  }

  protected hasAddendumDocument(): boolean {
    if (this.isCartMode()) {
      return this.documents().some(
        (document) =>
          document.DocumentType === EmployeeCartDocumentType.Addendum && !document.DisapprovedReason
      );
    }

    return hasDocument(
      this.benefit()?.EmployeeBenefitDocuments,
      EmployeeBenefitDocumentType.Addendum
    );
  }

  protected sign(): void {
    const request$ = this.createSignRequest();
    if (!request$) {
      return;
    }

    this.isLoading$.next(true);
    request$
      .pipe(
        finalize(() => this.isLoading$.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((response) => {
        if (response?.SignUrl) {
          openExternalUrl(response.SignUrl);
        }

        const docId = response?.DocumentIds?.[0];
        if (docId) {
          this.quillDocumentId$.next(docId);
          this.updateStatus(docId);
        }

        if (this.isCartMode()) {
          this.benefitAddendumSignStatusChanged.emit(false);
        }
      });
  }

  protected updateStatus(id: number): void {
    this.isLoading$.next(true);
    this.quillService
      .getStatus(id)
      .pipe(
        switchMap((data) => blobToText(new Blob([data]))),
        finalize(() => this.isLoading$.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res: QuillSignStatus) => this.processQuillStatus(res));
  }

  protected confirmQuillSigned(): void {
    // Cart-level signing has no confirm-quill-signed endpoint; the backend finalizes
    // the cart when Quill reports SIGNED via status polling / push notifications.
    if (this.isCartMode()) {
      return;
    }

    if (this.isLoading$.getValue()) {
      return;
    }

    const benefitId = this.benefit()?.Id;
    if (!benefitId) {
      return;
    }

    this.isLoading$.next(true);
    this.quillService
      .confirmBenefitSigned(benefitId)
      .pipe(
        catchError((error) => {
          this.isLoading$.next(false);
          return this.httpErrorHandlerService.handleError(error);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => {
        if (result === null) {
          this.isLoading$.next(false);
          return;
        }

        // Stay loading until the overview closes this panel after portfolio refresh.
        this.benefitAddendumSignStatusChanged.emit(true);
      });
  }

  private isCartMode(): boolean {
    return this.cartId() !== null;
  }

  private createSignRequest(): Observable<SignWithQuillResultDto> | null {
    if (this.isCartMode()) {
      const id = this.cartId();
      if (!id) {
        return null;
      }

      return this.cartDocumentsService.signCartDocument(id);
    }

    const benefitId = this.benefit()?.Id;
    if (!benefitId) {
      return null;
    }

    return this.quillService.signBenefitAddendum(benefitId);
  }

  private processQuillStatus(status: QuillSignStatus): void {
    const quillStatus = status?.toUpperCase();

    switch (quillStatus) {
      case 'DECLINED': {
        this.quillDocumentStatus$.next(null);
        this.quillDocumentId$.next(null);
        this.benefitAddendumSignStatusChanged.emit(false);
        break;
      }
      case 'SIGNED':
      case 'WAITING_FOR_SIGNATURES': {
        this.quillDocumentStatus$.next(quillStatus);
        this.benefitAddendumSignStatusChanged.emit(this.isCartMode() && quillStatus === 'SIGNED');
        break;
      }
      default: {
        this.benefitAddendumSignStatusChanged.emit(false);
      }
    }
  }
}
